import { useEditor } from '@candulabs/craft';

import { useLocalStorage } from 'commons/hooks';
import { useMutations } from 'craft/hooks/useMutations';
import { InsertMode } from 'craft/hooks/useMutations/types';
import { useSelection } from 'craft/hooks/useSelection';
import { SerializedNodeTree } from 'craft/types';

export const CANDU_CLIPBOARD = 'candu.clipboard';

export const useCopyCutAndPaste = () => {
  const { insertSerializedNodeTrees, deleteNode } = useMutations();
  const { query } = useEditor();
  const { getSerializedTree, selection } = useSelection();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setClipboard, getClipboard] = useLocalStorage(CANDU_CLIPBOARD, null);

  const getTrees = (nodeIds: string[]) => nodeIds.map((nodeId) => getSerializedTree(nodeId));

  return {
    canCopy: () =>
      !selection.isEmpty &&
      !selection.all.some((nodeId: string) => {
        const node = query.node(nodeId);
        if (!node) {
          return false;
        }
        return node.isDraggable() && node.isDroppable([nodeId]);
      }),
    onCopy: () => {
      if (selection.isEmpty) {
        return;
      }
      setClipboard({ trees: getTrees(selection.all) });
    },
    canCut: () =>
      !selection.isEmpty &&
      !selection.all.some((nodeId: string) => {
        const node = query.node(nodeId);
        if (!node) {
          return false;
        }
        return !node.isDeletable();
      }),
    onCut: () => {
      if (selection.isEmpty) {
        setClipboard(null);
        return;
      }
      setClipboard({ trees: getTrees(selection.all) });
      deleteNode(selection.all);
    },
    canPaste: () => {
      if (!getClipboard()) {
        return false;
      }

      const nodeToPasteAfter = selection.last; // since the onPaste calls insertSerializedNodeTrees on the selection.last
      if (nodeToPasteAfter === 'ROOT') {
        return false; // we cannot drop after Document node
      }

      const node = query.node(nodeToPasteAfter);
      if (!node) {
        return false;
      }

      const parent = node.getParent();

      if (!parent) {
        return false;
      }

      return parent.isDroppable(
        getClipboard().trees?.map((tree: SerializedNodeTree) => tree.rootNodeId),
      );
    },
    canPasteInto: () => !!getClipboard() && query.node(selection.last).isCanvas(),
    onPaste: (insertMode: InsertMode = InsertMode.AUTO) => {
      // handle this just in case
      if (selection.isEmpty) {
        return;
      }
      const clipboard = getClipboard();
      const { trees } = clipboard || {
        trees: null,
      };

      if (!trees) {
        return;
      }

      insertSerializedNodeTrees(trees, selection.last, insertMode);
    },
  };
};
